/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useNotifications } from '@/hooks/use-notifications'
import { ConfigDrawer } from '@/components/config-drawer'
import { LanguageSwitcher } from '@/components/language-switcher'
import { NotificationButton } from '@/components/notification-button'
import {
  ConsoleAnnouncementsDialog,
  SystemNoticeDialog,
} from '@/components/notification-dialog'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { SetupWizardButton } from '@/components/setup-wizard-button'
import { Header } from './header'
import { HeaderSearch } from './header-search'
import { SystemBrand } from './system-brand'

/**
 * General application Header component.
 *
 * Per the nova_portal design, the top bar carries ONLY right-side actions
 * (search, setup-wizard, contact-us, notifications, theme/lang, profile). The
 * previous in-bar navigation menu (TopNav) has been removed — primary
 * navigation lives in the sidebar.
 *
 * @example
 * // Basic usage
 * <AppHeader />
 *
 * @example
 * // Hide the search box
 * <AppHeader showSearch={false} />
 *
 * @example
 * // Fully customize left and right content
 * <AppHeader
 *   leftContent={<CustomLeft />}
 *   rightContent={<CustomRight />}
 * />
 */
type AppHeaderProps = {
  /**
   * Left content rendered right of the brand pill.
   */
  leftContent?: React.ReactNode
  /**
   * Whether to show search box
   * @default true
   */
  showSearch?: boolean
  /**
   * Custom right content, overrides default right content if provided
   */
  rightContent?: React.ReactNode
  /**
   * Whether to show the setup wizard entry-point button
   * @default true
   */
  showSetupWizard?: boolean
  /**
   * Whether to show notification button
   * @default true
   */
  showNotifications?: boolean
  /**
   * Whether to show config drawer
   * @default true
   */
  showConfigDrawer?: boolean
  /**
   * Whether to show profile dropdown
   * @default true
   */
  showProfileDropdown?: boolean
}

export function AppHeader({
  leftContent,
  showSearch = true,
  rightContent,
  showSetupWizard = true,
  showNotifications = true,
  showConfigDrawer = true,
  showProfileDropdown = true,
}: AppHeaderProps) {
  // Notifications hook
  const notifications = useNotifications()

  return (
    <>
      <Header>
        {/* Noya: inline brand pill sits right of sidebar-trigger */}
        <SystemBrand variant='inline' />

        {leftContent ? (
          <div className='ms-2 flex items-center'>{leftContent}</div>
        ) : null}

        {rightContent ?? (
          <div className='ms-auto flex items-center gap-0.5 sm:gap-1'>
            {showSearch && <HeaderSearch />}
            {showSetupWizard && <SetupWizardButton />}
            {showNotifications && (
              <>
                <NotificationButton
                  kind='notice'
                  unreadCount={notifications.unreadNoticeCount}
                  onClick={notifications.openNoticeDialog}
                />
                <NotificationButton
                  kind='announcements'
                  unreadCount={notifications.unreadAnnouncementsCount}
                  onClick={notifications.openAnnouncementsDialog}
                />
              </>
            )}
            <LanguageSwitcher />
            {showConfigDrawer && <ConfigDrawer />}
            {showProfileDropdown && <ProfileDropdown />}
          </div>
        )}
      </Header>

      {/* Notification Dialog */}
      {showNotifications && (
        <SystemNoticeDialog
          open={notifications.noticeDialogOpen}
          onOpenChange={notifications.setNoticeDialogOpen}
          notice={notifications.notice}
          loading={notifications.loading}
          onCloseToday={notifications.closeToday}
        />
      )}
      {showNotifications && (
        <ConsoleAnnouncementsDialog
          open={notifications.announcementsDialogOpen}
          onOpenChange={notifications.setAnnouncementsDialogOpen}
          announcements={notifications.announcements}
          loading={notifications.loading}
        />
      )}
    </>
  )
}
